/**
 * home-host-card.js - the shared host helpers on window.GlowmingHomeHost.
 *
 * It used to draw the homepage's full host card ("card C", signed off
 * 2026-09-24). That card never showed: inside the Welcome Block,
 * assets/welcome-block.js always hands a host visitor the COMPACT card, so the
 * full card, its drawing code and its snippet were retired on 2026-09-28
 * (operator ruling "Compact, preview matches", then "Remove it, push live").
 *
 * What stays is read by two callers:
 *   - assets/welcome-block.js: knownRef, refFromSearch, cardFrom, label, ICONS;
 *   - assets/journey-hosts.js (Meet our hosts): safeUrl, PHOTO_PREFIX,
 *     socialsFrom, ICONS.
 *
 * The Business App side is GET /api/landing-welcome?ref=<ref> (BA PR #1066):
 *   200 { status: 'off' }                             - no host
 *   200 { status: 'ok', mode, cards, priority, host } - host null = no host
 * Only approved text ever reaches it; it never carries a full surname, an
 * email or an id. Pure helpers, tested by tools/lib/home-host-card.test.mjs.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.GlowmingHomeHost) return;

  /** The Affiliate Tracker's 90 days (its storage key and cookie are glowming_affiliate_ref). */
  var TTL_MS = 90 * 86400000;
  /** The ref families the tracker and the Business App accept. */
  var REF_REGEX = /^(?:GA[IB]-\d{5,6}|GCM-\d{3,5}|[a-zA-Z0-9]{8,10})$/;
  /** First name + ONE initial (the Business App's public name shape). */
  var FIRST_AND_INITIAL = /^([A-Za-zÀ-ɏ][A-Za-zÀ-ɏ' -]{0,39}) ([A-Za-zÀ-ɏ])$/;
  /**
   * The ONLY photo origin accepted (Codex home r1, Medium): the Business App
   * signs affiliate photos with Supabase createSignedUrl on the production
   * project, which always yields this prefix.
   */
  var PHOTO_PREFIX = 'https://hajhlaprzsnubjrynljd.supabase.co/storage/v1/object/sign/';
  /**
   * Where each social may point: exactly the four sites the Business App builds
   * links for (landing-welcome-socials.ts). Anything else is dropped.
   */
  var SOCIAL_ORIGINS = {
    instagram: 'https://www.instagram.com/',
    facebook: 'https://www.facebook.com/',
    tiktok: 'https://www.tiktok.com/@',
    whatsapp: 'https://wa.me/'
  };
  var SOCIAL_NAMES = { instagram: 'Instagram', facebook: 'Facebook', tiktok: 'TikTok', whatsapp: 'WhatsApp' };
  /** The Glowming icon pack's social glyphs (the signed-off page's own). */
  var ICONS = {
    instagram: '<rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>',
    facebook: '<path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>',
    tiktok: '<path d="M9 12a4 4 0 104 4V4a5 5 0 005 5"/>',
    whatsapp: '<path d="M3.5 20.5l1.3-4.2A8.5 8.5 0 1112 20.5a8.4 8.4 0 01-4.1-1.1z"/><path d="M9 8.5c0 3.3 3.2 6.5 6.5 6.5l1-1.6-2-1-1 .9c-1-.4-2.4-1.8-2.8-2.8l.9-1-1-2z"/>'
  };

  /**
   * A theme-editor label with {name} replaced by the first name (operator
   * 2026-09-25: the cards' wording is editable in Shopify or the BA).
   */
  function label(template, first, fallback) {
    var t = typeof template === 'string' && template.trim() ? template.trim() : fallback;
    return t.split('{name}').join(first);
  }

  /** A plausible ref, or null. */
  function cleanRef(value) {
    var ref = typeof value === 'string' ? value.trim() : '';
    return REF_REGEX.test(ref) ? ref : null;
  }

  /**
   * The ref this visitor carries, in the tracker's order: stored (within 90
   * days), cookie (the raw ref, URI-encoded), then the cart attribute.
   */
  function knownRef(storedJson, cookieHeader, cartRef, now) {
    if (storedJson) {
      try {
        var parsed = JSON.parse(storedJson);
        var at = typeof now === 'number' ? now : Date.now();
        if (parsed && typeof parsed.ts === 'number' && at - parsed.ts <= TTL_MS) {
          var s = cleanRef(parsed.ref);
          if (s) return s;
        }
      } catch (e) { /* a corrupt entry is no entry */ }
    }
    var m = String(cookieHeader || '').match(/(?:^|;\s*)glowming_affiliate_ref=([^;]*)/);
    if (m) {
      try { var c = cleanRef(decodeURIComponent(m[1])); if (c) return c; } catch (e) { /* bad encoding */ }
    }
    return cleanRef(cartRef);
  }

  /**
   * The ref in the page address (?gs_ref=), for the FIRST page of a visit that
   * arrived through a host's link. The click tracker only stores the ref after
   * its own call returns, so on that first page the stored ref is not there
   * yet and the visitor saw the stranger cards instead of her host's card
   * (measured on the live site 2026-09-28). Used only when nothing is stored,
   * so the tracker's first-click-wins order is unchanged.
   */
  function refFromSearch(search) {
    var m = String(search || '').match(/[?&]gs_ref=([^&#]*)/);
    if (!m) return null;
    try { return cleanRef(decodeURIComponent(m[1].replace(/\+/g, ' '))); } catch (e) { return null; }
  }

  /** Trimmed text of at most `max` characters, or '' when absent. */
  function text(value, max) {
    return typeof value === 'string' ? value.trim().slice(0, max) : '';
  }

  /** A URL with no spaces, quotes or angle brackets that starts with one of `prefixes`. */
  function safeUrl(value, prefixes) {
    if (typeof value !== 'string' || !/^[^\s"'<>]+$/.test(value)) return null;
    for (var n = 0; n < prefixes.length; n++) if (value.indexOf(prefixes[n]) === 0) return value;
    return null;
  }

  /** The socials that may be shown, in the design's order. */
  function socialsFrom(socials) {
    var out = [];
    if (!socials || typeof socials !== 'object') return out;
    ['instagram', 'facebook', 'tiktok', 'whatsapp'].forEach(function (k) {
      var s = socials[k];
      var url = s && safeUrl(s.url, [SOCIAL_ORIGINS[k]]);
      if (url) out.push({ key: k, url: url, name: SOCIAL_NAMES[k] });
    });
    return out;
  }

  /**
   * The host a Business App answer names, or null for "no host" (the welcome
   * block then shows the stranger cards). The name must be "First L" (a
   * trailing period dropped), so no full surname is ever taken; only the FIRST
   * WORD of the first name is kept (Codex home r1: "Mary Jane V." -> "Mary").
   * The welcome block prints her words and socials from it.
   */
  function cardFrom(body) {
    if (!body || body.status !== 'ok' || !body.host) return null;
    var host = body.host;
    var name = text(host.displayName, 60).replace(/\.$/, '');
    var m = name.match(FIRST_AND_INITIAL);
    if (!m) return null;
    var first = m[1].split(' ')[0];
    var initial = m[2].toUpperCase();
    return {
      first: first,
      initial: initial,
      headline: text(host.headline, 60),
      message: text(host.welcomeMessage, 160),
      socials: socialsFrom(host.socials)
    };
  }

  window.GlowmingHomeHost = {
    knownRef: knownRef, refFromSearch: refFromSearch, cardFrom: cardFrom, label: label,
    socialsFrom: socialsFrom, safeUrl: safeUrl, PHOTO_PREFIX: PHOTO_PREFIX, ICONS: ICONS
  };
})();
